import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const api = axios.create({
  baseURL: `${API_BASE}/api`,
  headers: { 'Content-Type': 'application/json' }
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('funnely_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Don't redirect on login/register 401 - let the form handle it
      const url = error.config?.url || '';
      if (!url.includes('/auth/login') && !url.includes('/auth/register')) {
        localStorage.removeItem('funnely_token');
        localStorage.removeItem('funnely_user');
        if (!window.location.pathname.startsWith('/store') && !window.location.pathname.startsWith('/download')) {
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

export default api;

// Auth
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  me: () => api.get('/auth/me'),
};

// Products
export const productsAPI = {
  list: () => api.get('/products'),
  get: (id) => api.get(`/products/${id}`),
  create: (data) => api.post('/products', data),
  update: (id, data) => api.patch(`/products/${id}`, data),
  delete: (id) => api.delete(`/products/${id}`),
  createBundle: (data) => api.post('/products/bundle', data),
  updateBundle: (id, data) => api.patch(`/products/bundle/${id}`, data),
  refreshBundleThumbnail: (id) => api.post(`/products/bundle/${id}/refresh-thumbnail`),
};

// File Upload
export const uploadAPI = {
  file: (formData) => api.post('/upload/file', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  thumbnail: (formData) => api.post('/upload/thumbnail', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
};


// Courses
export const courseAPI = {
  list: () => api.get('/courses'),
  get: (id) => api.get(`/courses/${id}`),
  create: (data) => api.post('/courses', data),
  update: (id, data) => api.put(`/courses/${id}`, data),
  delete: (id) => api.delete(`/courses/${id}`),
  addModule: (id, data) => api.post(`/courses/${id}/modules`, data),
  updateModule: (id, mid, data) => api.put(`/courses/${id}/modules/${mid}`, data),
  deleteModule: (id, mid) => api.delete(`/courses/${id}/modules/${mid}`),
  addLesson: (id, mid, data) => api.post(`/courses/${id}/modules/${mid}/lessons`, data),
  updateLesson: (id, mid, lid, data) => api.put(`/courses/${id}/modules/${mid}/lessons/${lid}`, data),
  deleteLesson: (id, mid, lid) => api.delete(`/courses/${id}/modules/${mid}/lessons/${lid}`),
  reorderModules: (id, data) => api.post(`/courses/${id}/reorder-modules`, data),
  reorderLessons: (id, mid, data) => api.post(`/courses/${id}/modules/${mid}/reorder-lessons`, data),
  publish: (id) => api.post(`/courses/${id}/publish`),
  unpublish: (id) => api.post(`/courses/${id}/unpublish`),
  verifyAccess: (data) => api.post('/course-access/verify', data),
  getByToken: (token) => api.get(`/course-access/${token}`),
  updateProgress: (token, data) => api.post(`/course-access/${token}/progress`, data),
};

// Digital Studio
export const studioAPI = {
  upload: (formData) => api.post('/studio/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  niches: () => api.get('/studio/niches'),
  saveInterview: (projectId, data) => api.post(`/studio/${projectId}/interview`, data),
  generateDraft: (projectId) => api.post(`/studio/${projectId}/generate-draft`),
  getDraft: (projectId) => api.get(`/studio/${projectId}/draft`),
  saveDraft: (projectId, data) => api.post(`/studio/${projectId}/save-draft`, data),
  regenerateChapter: (projectId, data) => api.post(`/studio/${projectId}/regenerate-chapter`, data),
  chapterStatus: (projectId) => api.get(`/studio/${projectId}/chapter-status`),
  getProject: (projectId) => api.get(`/studio/${projectId}/full`),
  saveDesignEdits: (projectId, data) => api.post(`/studio/${projectId}/save-design-edits`, data),
  generate: (projectId, data) => api.post(`/studio/${projectId}/generate`, data),
  status: (projectId) => api.get(`/studio/${projectId}/status`),
  submitIdea: (data) => api.post('/studio/idea', data),
  exportPdf: (projectId, data) => api.post(`/studio/${projectId}/export`, data, { responseType: 'blob', timeout: 120000 }),
  exportHtml: (projectId, data) => api.post(`/studio/${projectId}/export-html`, data, { responseType: 'blob' }),
  exportDocx: (projectId, data) => api.post(`/studio/${projectId}/export-docx`, data, { responseType: 'blob' }),
  projects: () => api.get('/studio/projects'),
  deleteProject: (projectId) => api.delete(`/studio/${projectId}`),
  listProduct: (projectId, data) => api.post(`/studio/${projectId}/list-product`, data),
  unlistProduct: (projectId) => api.post(`/studio/${projectId}/unlist-product`),
};

// Landing Pages
export const landingAPI = {
  templates: () => api.get('/landing-pages/templates'),
  list: () => api.get('/landing-pages'),
  get: (id) => api.get(`/landing-pages/${id}`),
  create: (data) => api.post('/landing-pages', data),
  update: (id, data) => api.patch(`/landing-pages/${id}`, data),
  publish: (id) => api.post(`/landing-pages/${id}/publish`),
  duplicate: (id) => api.post(`/landing-pages/${id}/duplicate`),
  delete: (id) => api.delete(`/landing-pages/${id}`),
  aiGenerate: (data) => api.post('/landing-pages/ai-generate', data),
};

// Customers
export const customersAPI = {
  list: () => api.get('/customers'),
  get: (id) => api.get(`/customers/${id}`),
};

// Orders
export const ordersAPI = {
  list: (params) => api.get('/orders', { params }),
  get: (id) => api.get(`/orders/${id}`),
  reissueLink: (id) => api.post(`/orders/${id}/reissue-link`),
  revoke: (id) => api.post(`/orders/${id}/revoke`),
};

// Reports
export const reportsAPI = {
  summary: (params) => api.get('/reports/summary', { params }),
  exportReport: (format, params) => api.get('/reports/export', { params: { format, ...params }, responseType: 'blob' }),
};

// AI Chat
export const chatAPI = {
  send: (message, session_id) => api.post('/chat', { message, session_id }),
  history: (limit = 50) => api.get('/chat/history', { params: { limit } }),
};

// Payment Settings
export const settingsAPI = {
  getPayments: () => api.get('/settings/payments'),
  updatePayments: (data) => api.put('/settings/payments', data),
};

// Checkout Forms
export const checkoutFormsAPI = {
  list: () => api.get('/checkout-forms'),
  get: (id) => api.get(`/checkout-forms/${id}`),
  create: (data) => api.post('/checkout-forms', data),
  update: (id, data) => api.patch(`/checkout-forms/${id}`, data),
  delete: (id) => api.delete(`/checkout-forms/${id}`),
};

// Profile
export const profileAPI = {
  update: (data) => api.patch('/profile', data),
};

// Plan usage
export const planAPI = {
  usage: () => api.get('/plan/usage'),
};

// Creator gateway status
export const gatewayAPI = {
  status: () => api.get('/creator/gateway-status'),
};

// Public Store
export const storeAPI = {
  checkout: (productId, data) => api.post(`/checkout/${productId}`, data),
  checkoutMulti: (data) => api.post(`/checkout/multi`, data),
  captureCheckoutIntent: (data) => api.post(`/checkout/capture-intent`, data),
  downloadInfo: (token) => api.get(`/download-info/${token}`),
  thankYou: (sessionId) => api.get(`/thank-you/${sessionId}`),
  getLandingPage: (pageId) => api.get(`/store/${pageId}`),
  getLandingPageByHandle: (handle, slug) => api.get(`/store/${handle}/${slug}`),
  trackView: (data) => api.post(`/track/view`, data),
  checkGateway: (creatorId) => api.get(`/payment/check-gateway/${creatorId}`),
  createBillplzBill: (data) => api.post(`/payment/billplz/create-bill`, data),
  createToyyibpayBill: (data) => api.post(`/payment/toyyibpay/create-bill`, data),
  createSenangpayPayment: (data) => api.post(`/payment/senangpay/create-payment`, data),
  createStripeSession: (data) => api.post(`/payment/stripe/create-session`, data),
};
